import { Fragment, useState, useEffect } from "react";
import { useAuth, useUser } from "@clerk/react";
import { useNavigate, Link } from "@tanstack/react-router";
import { TopBar } from "../components/TopBar";
import adminStyles from "../styles/components/AdminPage.module.css";
import styles from "../styles/pages/AdminSiteMapPage.module.css";

interface RouteParam {
  name: string;
  placeholder: string;
}

interface RouteEntry {
  group: "App" | "Settings" | "Admin";
  path: string;
  purpose: string;
  params: RouteParam[];
  desktop: boolean;
}

const ROUTES: RouteEntry[] = [
  {
    group: "App",
    path: "/",
    purpose: "Default landing page — home feed",
    params: [],
    desktop: false,
  },
  {
    group: "App",
    path: "/$roomSlug",
    purpose: "System or personal room feed — slug must start with ~",
    params: [{ name: "roomSlug", placeholder: "~tech" }],
    desktop: false,
  },
  {
    group: "App",
    path: "/profile",
    purpose: "Signed-in user profile and account settings",
    params: [],
    desktop: false,
  },
  {
    group: "App",
    path: "/premium",
    purpose: "Premium plan upgrade and feature overview",
    params: [],
    desktop: false,
  },
  {
    group: "App",
    path: "/my-rooms",
    purpose: "List and manage personal rooms",
    params: [],
    desktop: false,
  },
  {
    group: "App",
    path: "/my-rooms/edit/$roomId",
    purpose: "Edit a personal room — title, sources, public sharing",
    params: [{ name: "roomId", placeholder: "room-id" }],
    desktop: true,
  },
  {
    group: "App",
    path: "/source/$sourceId",
    purpose: "Feed source detail page with recent items",
    params: [{ name: "sourceId", placeholder: "bbc-news" }],
    desktop: false,
  },
  {
    group: "App",
    path: "/source/$sourceId/edit",
    purpose: "User-facing feed editor for a source",
    params: [{ name: "sourceId", placeholder: "bbc-news" }],
    desktop: true,
  },
  {
    group: "Settings",
    path: "/settings/digest",
    purpose: "Email digest schedule and content preferences",
    params: [],
    desktop: false,
  },
  {
    group: "Settings",
    path: "/settings/filters",
    purpose: "Keyword-based content filters",
    params: [],
    desktop: false,
  },
  {
    group: "Settings",
    path: "/settings/webhooks",
    purpose: "Outbound webhook endpoints",
    params: [],
    desktop: false,
  },
  {
    group: "Settings",
    path: "/settings/rules",
    purpose: "Automation rules — tag, filter, and forward items",
    params: [],
    desktop: false,
  },
  {
    group: "Settings",
    path: "/settings/integrations",
    purpose: "Third-party integrations — Slack, Discord, Teams",
    params: [],
    desktop: false,
  },
  {
    group: "Settings",
    path: "/team",
    purpose: "Shared team workspace and member management",
    params: [],
    desktop: false,
  },
  {
    group: "Admin",
    path: "/admin",
    purpose: "Admin dashboard — ops panel, stats, room and source inspector",
    params: [],
    desktop: true,
  },
  {
    group: "Admin",
    path: "/admin/feeds",
    purpose: "Browse and search all catalog feed sources",
    params: [],
    desktop: true,
  },
  {
    group: "Admin",
    path: "/admin/feeds/$sourceId",
    purpose: "Edit a catalog feed — URL, title, tags, thumbnails, config",
    params: [{ name: "sourceId", placeholder: "bbc-news" }],
    desktop: true,
  },
  {
    group: "Admin",
    path: "/admin/source/$sourceId/editor",
    purpose: "Inspect raw snapshot data and config for a source",
    params: [{ name: "sourceId", placeholder: "bbc-news" }],
    desktop: true,
  },
  {
    group: "Admin",
    path: "/admin/rooms",
    purpose: "Browse all system rooms",
    params: [],
    desktop: true,
  },
  {
    group: "Admin",
    path: "/admin/rooms/$roomId",
    purpose: "Edit a system room — sources, ordering, refresh interval",
    params: [{ name: "roomId", placeholder: "tech" }],
    desktop: true,
  },
  {
    group: "Admin",
    path: "/admin/user_feedback",
    purpose: "Review outstanding user-submitted feedback",
    params: [],
    desktop: true,
  },
  {
    group: "Admin",
    path: "/admin/reported_issues",
    purpose: "Review user-reported feed issues",
    params: [],
    desktop: true,
  },
  {
    group: "Admin",
    path: "/admin/catalog/import",
    purpose: "Import an OPML file into the pending catalog queue",
    params: [],
    desktop: true,
  },
  {
    group: "Admin",
    path: "/admin/catalog/review",
    purpose: "Review and approve or reject pending catalog submissions",
    params: [],
    desktop: true,
  },
  {
    group: "Admin",
    path: "/admin/sitemap",
    purpose: "All frontend routes — this page",
    params: [],
    desktop: true,
  },
];

const GROUPS = ["App", "Settings", "Admin"] as const;

const grouped = GROUPS.map((group) => ({
  group,
  routes: ROUTES.map((entry, idx) => ({ entry, idx })).filter(
    ({ entry }) => entry.group === group,
  ),
}));

function PathDisplay({ path }: { path: string }) {
  const parts = path.split(/(\$\w+)/);
  return (
    <>
      {parts.map((part, i) =>
        part.startsWith("$") ? (
          <span key={i} className={styles.paramSegment}>
            {part}
          </span>
        ) : (
          <span key={i}>{part}</span>
        ),
      )}
    </>
  );
}

export function AdminSiteMapPage() {
  const { isLoaded, userId } = useAuth();
  const navigate = useNavigate();
  const { user } = useUser();
  const role = (user?.publicMetadata as { role?: string } | undefined)?.role;
  const isAdmin = role === "admin" || role === "editor";
  const [paramValues, setParamValues] = useState<Record<string, string>>({});

  useEffect(() => {
    if (isLoaded && (!userId || !isAdmin)) {
      navigate({ to: "/" });
    }
  }, [isLoaded, userId, isAdmin, navigate]);

  if (!isLoaded || !userId || !isAdmin) return null;

  function setParam(routeIdx: number, paramName: string, value: string) {
    setParamValues((prev) => ({
      ...prev,
      [`${routeIdx}-${paramName}`]: value,
    }));
  }

  function getParam(routeIdx: number, paramName: string): string {
    return paramValues[`${routeIdx}-${paramName}`] ?? "";
  }

  function resolveUrl(entry: RouteEntry, idx: number): string | null {
    if (entry.params.length === 0) return entry.path;
    let url = entry.path;
    for (const p of entry.params) {
      const val = getParam(idx, p.name).trim();
      if (!val) return null;
      url = url.replace(`$${p.name}`, encodeURIComponent(val));
    }
    return url;
  }

  return (
    <div className={styles.page}>
      <TopBar />
      <div className={styles.content}>
        <nav className={styles.header}>
          <Link to="/admin" className={styles.back}>
            ← Admin
          </Link>
          <h1 className={styles.title}>Site Map</h1>
          <span className={styles.count}>{ROUTES.length} routes</span>
        </nav>

        <div className={adminStyles.panel}>
          <div className={adminStyles.panelHeader}>
            <h2 className={adminStyles.panelTitle}>All Routes</h2>
          </div>
          <div className={styles.tableWrap}>
            <table className={adminStyles.table}>
              <thead>
                <tr>
                  <th>Path</th>
                  <th>Purpose</th>
                  <th>Parameters</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {grouped.map(({ group, routes }) => (
                  <Fragment key={group}>
                    <tr className={styles.groupRow}>
                      <td colSpan={4} className={styles.groupCell}>
                        {group}
                      </td>
                    </tr>
                    {routes.map(({ entry, idx }) => {
                      const href = resolveUrl(entry, idx);
                      return (
                        <tr key={entry.path}>
                          <td className={styles.pathCell}>
                            <code className={styles.routeCode}>
                              <PathDisplay path={entry.path} />
                            </code>
                            {entry.desktop && (
                              <span className={styles.desktopBadge}>
                                desktop
                              </span>
                            )}
                          </td>
                          <td className={styles.purposeCell}>
                            {entry.purpose}
                          </td>
                          <td className={styles.paramsCell}>
                            {entry.params.length === 0 ? (
                              <span className={styles.noParams}>—</span>
                            ) : (
                              <div className={styles.paramList}>
                                {entry.params.map((p) => (
                                  <span key={p.name} className={styles.paramRow}>
                                    <code className={styles.paramName}>
                                      {p.name}
                                    </code>
                                    <input
                                      className={styles.paramInput}
                                      value={getParam(idx, p.name)}
                                      onChange={(e) =>
                                        setParam(idx, p.name, e.target.value)
                                      }
                                      placeholder={p.placeholder}
                                    />
                                  </span>
                                ))}
                              </div>
                            )}
                          </td>
                          <td className={styles.goCell}>
                            {href ? (
                              <a href={href} className={styles.goLink} target="_blank" rel="noopener noreferrer">
                                Go →
                              </a>
                            ) : (
                              <span className={styles.goDisabled}>Go →</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
